// ABOUTME: Renders the phone-sized preview for a WhatsApp template.
// ABOUTME: Keeps long template text and actions visible within the preview.
import React from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

type WhatsappTemplatePreviewProps = {
    bodyText: string;
    parameterDefinitions: Compleo.IObject[];
    headerText?: string;
    footerText?: string;
    buttons?: Compleo.IObject[];
    emptyButtonLabel: string;
};

const phoneStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 320,
    minHeight: "clamp(0px, calc(100vh - 180px), 420px)",
    maxHeight: "min(720px, calc(100vh - 180px))",
    borderRadius: 28,
    border: "10px solid #202124",
    boxSizing: "border-box",
    backgroundColor: "#e5ddd5",
    display: "flex",
    flexDirection: "column",
    margin: "0 auto",
    overflow: "hidden",
    boxShadow: "0 10px 24px rgba(0,0,0,0.18)"
};

const headerStyle: React.CSSProperties = {
    backgroundColor: "#075e54",
    color: "#fff",
    flexShrink: 0,
    padding: "14px 16px",
    fontWeight: 600
};

const chatStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflowX: "hidden",
    overflowY: "auto"
};

const messageStyle: React.CSSProperties = {
    backgroundColor: "#dcf8c6",
    borderRadius: 8,
    boxSizing: "border-box",
    maxWidth: "100%",
    padding: 12,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    boxShadow: "0 1px 2px rgba(0,0,0,0.16)"
};

const buttonStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: 6,
    color: "#128c7e",
    fontWeight: 600,
    marginTop: 6,
    padding: "7px 10px",
    textAlign: "center",
    overflowWrap: "anywhere",
    wordBreak: "break-word"
};

const interpolateTemplate = (
    bodyText: string,
    parameterDefinitions: Compleo.IObject[]
) => {
    const examplesByIndex = new Map<string, string>();

    for (const definition of parameterDefinitions) {
        examplesByIndex.set(
            String(definition.index || ""),
            String(definition.example || "")
        );
    }

    return String(bodyText || "").replace(/\{\{(\d+)\}\}/g, (_, index) => {
        const example = examplesByIndex.get(String(index));
        return example || `{{${index}}}`;
    });
};

export const WhatsappTemplatePreview = ({
    bodyText,
    parameterDefinitions,
    headerText,
    footerText,
    buttons = [],
    emptyButtonLabel
}: WhatsappTemplatePreviewProps) => (
    <Box style={phoneStyle}>
        <Box style={headerStyle}>WhatsApp</Box>
        <Box p={2} style={chatStyle}>
            <Box style={messageStyle}>
                {headerText && (
                    <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 700, marginBottom: 8 }}
                    >
                        {interpolateTemplate(headerText, parameterDefinitions)}
                    </Typography>
                )}
                {interpolateTemplate(bodyText, parameterDefinitions)}
                {footerText && (
                    <Typography
                        variant="caption"
                        component="div"
                        style={{ marginTop: 10, color: "#607d6b" }}
                    >
                        {footerText}
                    </Typography>
                )}
                {buttons.length > 0 && (
                    <Box mt={2}>
                        {buttons.map((button, index) => (
                            <Box
                                key={`${button.text}-${index}`}
                                style={buttonStyle}
                            >
                                {String(button.text || emptyButtonLabel)}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    </Box>
);
